import { useState } from 'react'

import {
  IconX,
  IconCheck,
  IconSparkles,
  IconChevronUp,
} from '@tabler/icons-react'
import { Interweave } from 'interweave'
import styled from 'styled-components'
import { Editor } from 'tinymce'

import { AIButton } from '../shared-components/AIButton'
import { SimpleAIGeneratedPill } from '../shared-components/AIGeneratedPill'
import { Button, Loading } from '../shared-components/BUI/Button'
import { Popover, PopoverItem } from '../shared-components/BUI/Popover'
import { Flexbox } from '../shared-components/Flexbox'

import { useUser } from '@/context/userContext'
import { useRegisterAIToolUsage } from '@/hooks/useRegisterAIToolUsage'

const BEAUTIFY_ACTIONS = {
  IMPROVE: 'improve',
  SHORTEN: 'shorten',
  DOT_POINTS: 'dotPoints',
} as const

type BeautifyMode = (typeof BEAUTIFY_ACTIONS)[keyof typeof BEAUTIFY_ACTIONS]

type Props = {
  editorRef: React.MutableRefObject<Editor>
  onChange?: (value: string) => void
}

const SuggestionWrapper = styled.div`
  margin-top: 0.5rem;
  padding: 1rem 1.5rem;
  max-width: 600px;
`

export const InputBeautify: React.FC<Props> = ({ editorRef, onChange }) => {
  const [isLoading, setIsLoading] = useState(false)
  const [extractedText, setExtractedText] = useState<string | null>(null)
  const [suggestion, setSuggestion] = useState<string | null>(null)
  const [isOpen, setIsOpen] = useState(false)
  const user = useUser()
  const { registerUsage } = useRegisterAIToolUsage('inputBeautify')

  if (!user?.aiTools?.aiToolsEnabled) {
    return
  }

  const scanText = async (selectedFile: File) => {
    setIsLoading(true)
    try {
      registerUsage('scan text', `${selectedFile.name}`)
      const formData = new FormData()
      formData.append('file', selectedFile)
      formData.append('media', selectedFile.type)

      const response = await fetch('/api/ai/imageToText', {
        method: 'POST',
        body: formData,
      })

      const data = await response.json()

      if (data.error) {
        throw new Error(data.error)
      }

      setExtractedText(data.text)
    } catch (error) {
      console.error('Error scanning text:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const selectedFile = e.target.files[0]
      setIsOpen(true)
      scanText(selectedFile)
    }
  }

  const handleExtractedText = (text: string, shouldAppend = false) => {
    if (!editorRef.current) return

    const newContent = shouldAppend
      ? `${editorRef.current.getContent()}<br/>${text}`
      : text

    editorRef.current.setContent(newContent)
    onChange?.(newContent)
  }

  const handleAccept = () => {
    if (extractedText) {
      handleExtractedText(extractedText, true)
      setExtractedText(null)
      setIsOpen(false)

      const fileInput = document.getElementById(
        'imageInput'
      ) as HTMLInputElement
      if (fileInput) {
        fileInput.value = ''
      }
    } else if (suggestion) {
      handleExtractedText(suggestion, false)
      setSuggestion(null)
      setIsOpen(false)
    }
  }

  const handleDismiss = () => {
    setExtractedText(null)
    setSuggestion(null)
    setIsOpen(false)
    const fileInput = document.getElementById('imageInput') as HTMLInputElement
    if (fileInput) {
      fileInput.value = ''
    }
  }

  const handleBeautifyAction = async (mode: BeautifyMode) => {
    if (!editorRef.current) return

    setIsLoading(true)
    try {
      registerUsage('beautify input: ', `${mode}`)
      const response = await fetch('/api/ai/beautify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          content: editorRef.current.getContent(),
          mode,
        }),
      })

      const data = await response.json()
      setSuggestion(data.improvedContent)
      setIsOpen(true)
    } catch (error) {
      console.error('Error beautifying text:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const renderContent = () => {
    if (isLoading) {
      return (
        <PopoverItem>
          <Loading />
        </PopoverItem>
      )
    }

    if (extractedText || suggestion) {
      return (
        <SuggestionWrapper>
          <Interweave content={extractedText || suggestion} />
          <Flexbox gap="0.5rem" justifyContent="space-between" mt="1.5rem">
            <SimpleAIGeneratedPill />
            <Flexbox gap="0.5rem">
              <Button
                variant="secondary-text"
                size="small"
                icon={<IconX size={16} />}
                onClick={handleDismiss}
              >
                Dismiss
              </Button>
              <Button
                variant="primary"
                size="small"
                icon={<IconCheck size={16} />}
                onClick={handleAccept}
              >
                Accept
              </Button>
            </Flexbox>
          </Flexbox>
        </SuggestionWrapper>
      )
    }

    return (
      <>
        <PopoverItem
          onClick={() => document.getElementById('imageInput')?.click()}
        >
          📷 Scan Notes
        </PopoverItem>
        <PopoverItem
          onClick={() => handleBeautifyAction(BEAUTIFY_ACTIONS.IMPROVE)}
        >
          🔧 Improve
        </PopoverItem>
        <PopoverItem
          onClick={() => handleBeautifyAction(BEAUTIFY_ACTIONS.SHORTEN)}
        >
          🤏 Shorten
        </PopoverItem>
        <PopoverItem
          onClick={() => handleBeautifyAction(BEAUTIFY_ACTIONS.DOT_POINTS)}
        >
          🔗 Dot points
        </PopoverItem>
      </>
    )
  }

  return (
    <>
      <Popover
        open={isOpen}
        onOpenChange={(open) => {
          if (!isLoading) {
            setIsOpen(open)
          }
        }}
        trigger="hover"
        content={renderContent()}
      >
        <AIButton
          variant="primary"
          size="medium"
          icon={<IconSparkles size={16} />}
          type="button"
        >
          <IconChevronUp size={14} />
        </AIButton>
      </Popover>
      <input
        id="imageInput"
        type="file"
        onChange={handleFileChange}
        accept="image/*"
        disabled={isLoading}
        style={{ display: 'none' }}
      />
    </>
  )
}
